
import type { FreeLessonTopic } from '../components/LessonViewer';

export const algebra1FreeLessons: FreeLessonTopic[] = [
  {
    id: 'real-numbers',
    title: 'The Real Number System',
    icon: 'ri-numbers-line',
    color: 'orange',
    lessons: [
      {
        id: 'classifying-numbers',
        title: 'Classifying Real Numbers',
        subtitle: 'Natural, Whole, Integer, Rational, Irrational',
        icon: 'ri-list-check-2',
        color: 'orange',
        keyFormulas: [
          'Natural: 1,2,3...',
          'Whole: 0,1,2,3...',
          'Integer: ...−2,−1,0,1,2...',
          'Rational: a/b (b≠0)',
          'Irrational: non-repeating, non-terminating',
        ],
        textbookExplanation: `Real numbers are organized into a hierarchy of sets, each building on the previous.

**Natural Numbers (N):** The counting numbers: 1, 2, 3, 4, 5, ...
These are the first numbers humans ever used — for counting objects.

**Whole Numbers (W):** Natural numbers plus zero: 0, 1, 2, 3, 4, ...
Every natural number is also a whole number.

**Integers (Z):** Whole numbers and their negatives: ..., −3, −2, −1, 0, 1, 2, 3, ...
Every whole number is also an integer.

**Rational Numbers (Q):** Any number expressible as a fraction a/b where a and b are integers and b ≠ 0.
- Terminating decimals: 0.5 = 1/2 ✓
- Repeating decimals: 0.333... = 1/3 ✓
- All integers are rational: 7 = 7/1 ✓

**Irrational Numbers:** Numbers that CANNOT be written as a fraction.
- Their decimals go on forever WITHOUT repeating.
- Examples: π ≈ 3.14159..., √2 ≈ 1.41421..., √3, e

**Real Numbers (R):** The union of ALL rational and irrational numbers.

The sets are nested: N ⊂ W ⊂ Z ⊂ Q ⊂ R`,
        shortMethod: `**Mr. Augustine's "NWIZR" Trick:**

Think of the letters N-W-I-Z-R as a staircase going UP:

**N** = Natural (1, 2, 3...)
**W** = Whole (add 0)
**I** = Integer (add negatives)
**Z** = raZional (add fractions & decimals) — Z for the fraction bar!
**R** = Real (add irrationals like √2, π)

**Quick Classification Test:**
1. Is it a perfect square root or whole number? → Natural/Whole/Integer
2. Can you write it as a fraction? → Rational
3. Is it √(non-perfect square) or π? → Irrational

**Golden Rule:** Every number belongs to Real. Work DOWN the list to find the most specific category.`,
        workedExamples: [
          {
            question: 'Classify √16 in as many ways as possible.',
            textbookSteps: [
              'Simplify: √16 = 4',
              'Is 4 a natural number? Yes — it is a positive counting number.',
              'Is 4 a whole number? Yes — all natural numbers are whole.',
              'Is 4 an integer? Yes — all whole numbers are integers.',
              'Is 4 rational? Yes — 4 = 4/1.',
              'Is 4 real? Yes — all rational numbers are real.',
            ],
            shortcut: "√16 = 4. It's a clean whole number, so it hits ALL categories: Natural, Whole, Integer, Rational, Real.",
            answer: 'Natural, Whole, Integer, Rational, Real',
          },
          {
            question: 'Classify √7.',
            textbookSteps: [
              '7 is not a perfect square (1,4,9,16,25... — 7 is not on this list).',
              'So √7 is irrational: √7 ≈ 2.6457513...',
              'The decimal never terminates and never repeats.',
              'It cannot be written as a fraction a/b.',
              'It IS a real number (all irrationals are real).',
            ],
            shortcut: 'Ask: "Is the number under the root a perfect square?" No → Irrational and Real. Done!',
            answer: 'Irrational, Real',
          },
        ],
        practiceQuestions: [
          { question: 'Classify the number 9.', steps: ['9 is a positive counting number → Natural', 'All natural numbers are whole → Whole', 'All whole numbers are integers → Integer', '9 = 9/1 → Rational', 'All rationals are real → Real'], shortcut: '9 is a clean positive whole number — it hits ALL 5 categories.', answer: 'Natural, Whole, Integer, Rational, Real' },
          { question: 'Classify the number −3.', steps: ['−3 is negative → NOT Natural', '−3 is negative → NOT Whole', '−3 is a negative integer → Integer ✓', '−3 = −3/1 → Rational ✓', 'All rationals are real → Real ✓'], shortcut: 'Negative whole number = Integer, Rational, Real. Three categories only.', answer: 'Integer, Rational, Real' },
          { question: 'Classify the number 0.', steps: ['0 is not a counting number → NOT Natural', '0 is the starting whole number → Whole ✓', 'All whole numbers are integers → Integer ✓', '0 = 0/1 → Rational ✓', 'All rationals are real → Real ✓'], shortcut: 'Zero is special — it starts at Whole (not Natural). Whole, Integer, Rational, Real.', answer: 'Whole, Integer, Rational, Real' },
          { question: 'Classify the number 2/5.', steps: ['2/5 is not a whole number → NOT Natural, Whole, or Integer', '2/5 is already a fraction a/b → Rational ✓', 'All rationals are real → Real ✓'], shortcut: "It's already a fraction → Rational and Real.", answer: 'Rational, Real' },
          { question: 'Classify the number π.', steps: ['π ≈ 3.14159265... — decimal never ends, never repeats', 'Cannot be written as a fraction → NOT Rational', 'It IS a real number → Irrational, Real'], shortcut: 'π is the classic irrational. Memorize: π → Irrational, Real.', answer: 'Irrational, Real' },
          { question: 'Classify the number −√4.', steps: ['Simplify: √4 = 2, so −√4 = −2', '−2 is negative → NOT Natural or Whole', '−2 is a negative integer → Integer ✓', '−2 = −2/1 → Rational ✓', 'Real ✓'], shortcut: 'Simplify first! −√4 = −2. Negative whole number = Integer, Rational, Real.', answer: 'Integer, Rational, Real' },
          { question: 'Classify the number 0.75.', steps: ['0.75 is a terminating decimal', '0.75 = 75/100 = 3/4 → Rational ✓', 'Not a whole number → NOT Natural, Whole, Integer', 'Real ✓'], shortcut: 'Terminating decimal → always Rational and Real.', answer: 'Rational, Real' },
          { question: 'Classify the number √5.', steps: ['5 is not a perfect square', '√5 ≈ 2.2360679... — non-terminating, non-repeating', 'Cannot be written as a fraction → Irrational ✓', 'Real ✓'], shortcut: '√(non-perfect square) → always Irrational and Real.', answer: 'Irrational, Real' },
          { question: 'Classify the number −7/1.', steps: ['−7/1 = −7', '−7 is a negative integer → Integer ✓', '−7 = −7/1 → Rational ✓', 'Real ✓'], shortcut: 'Simplify: −7/1 = −7. Negative integer = Integer, Rational, Real.', answer: 'Integer, Rational, Real' },
          { question: 'Classify the number 0.1̄ (0.111...).', steps: ['0.111... is a repeating decimal', 'Repeating decimals are always rational: 0.111... = 1/9', 'Not a whole number → NOT Natural, Whole, Integer', 'Real ✓'], shortcut: 'Repeating decimal → always Rational and Real.', answer: 'Rational, Real' },
          { question: 'Classify the number √36.', steps: ['√36 = 6 (perfect square)', '6 is a positive counting number → Natural ✓', 'Whole ✓, Integer ✓, Rational ✓, Real ✓'], shortcut: '√36 = 6. Clean whole number → all 5 categories.', answer: 'Natural, Whole, Integer, Rational, Real' },
          { question: 'Classify the number −√2.', steps: ['2 is not a perfect square → √2 is irrational', '−√2 is the negative of an irrational → still Irrational ✓', 'Real ✓'], shortcut: 'Negative of an irrational is still irrational. → Irrational, Real.', answer: 'Irrational, Real' },
          { question: 'Classify the number 100.', steps: ['100 is a positive counting number → Natural ✓', 'Whole ✓, Integer ✓, Rational ✓, Real ✓'], shortcut: 'Big positive whole number → all 5 categories.', answer: 'Natural, Whole, Integer, Rational, Real' },
          { question: 'Classify the number −0.25.', steps: ['−0.25 = −1/4 (terminating decimal)', 'Not a whole number → NOT Natural, Whole, Integer', 'Can be written as a fraction → Rational ✓', 'Real ✓'], shortcut: 'Negative terminating decimal → Rational and Real.', answer: 'Rational, Real' },
          { question: 'Classify the number √49.', steps: ['√49 = 7 (perfect square)', '7 is a positive counting number → Natural ✓', 'Whole ✓, Integer ✓, Rational ✓, Real ✓'], shortcut: '√49 = 7. Clean whole number → all 5 categories.', answer: 'Natural, Whole, Integer, Rational, Real' },
          { question: 'Classify the number 3.14.', steps: ['3.14 is a terminating decimal', '3.14 = 314/100 = 157/50 → Rational ✓', 'Not a whole number → NOT Natural, Whole, Integer', 'Real ✓', 'NOTE: 3.14 ≠ π. 3.14 is rational; π is irrational!'], shortcut: '3.14 terminates → Rational and Real. Do NOT confuse with π!', answer: 'Rational, Real' },
          { question: 'Classify the number −15.', steps: ['−15 is negative → NOT Natural or Whole', '−15 is a negative integer → Integer ✓', '−15 = −15/1 → Rational ✓', 'Real ✓'], shortcut: 'Negative whole number → Integer, Rational, Real.', answer: 'Integer, Rational, Real' },
          { question: 'Classify the number √(−9).', steps: ['You cannot take the square root of a negative number in the real number system', '√(−9) = 3i (imaginary number)', 'Imaginary numbers are NOT real numbers'], shortcut: "Negative under a square root → NOT a real number. It's imaginary!", answer: 'Not a Real Number (Imaginary)' },
          { question: 'Classify the number 22/7.', steps: ['22/7 is already written as a fraction a/b → Rational ✓', '22 ÷ 7 ≈ 3.142857142857... (repeating) → confirms rational', 'Not a whole number → NOT Natural, Whole, Integer', 'Real ✓', 'IMPORTANT: 22/7 ≠ π. 22/7 is rational; π is irrational!'], shortcut: '22/7 is a fraction → Rational and Real. It approximates π but is NOT π.', answer: 'Rational, Real' },
          { question: 'Classify the number √(1/4).', steps: ['√(1/4) = √1/√4 = 1/2', '1/2 is a fraction → Rational ✓', 'Not a whole number → NOT Natural, Whole, Integer', 'Real ✓'], shortcut: "Simplify: √(1/4) = 1/2. It's a fraction → Rational and Real.", answer: 'Rational, Real' },
          { question: 'Classify the number 0.010010001... (non-repeating).', steps: ['The decimal goes on forever without repeating', 'Cannot be written as a fraction → Irrational ✓', 'Real ✓'], shortcut: 'Non-terminating AND non-repeating decimal → always Irrational and Real.', answer: 'Irrational, Real' },
        ],
      },
      {
        id: 'order-of-operations',
        title: 'Order of Operations (PEMDAS)',
        subtitle: 'Parentheses, Exponents, Multiply/Divide, Add/Subtract',
        icon: 'ri-sort-asc',
        color: 'orange',
        keyFormulas: ['P → Parentheses', 'E → Exponents', 'MD → Multiply & Divide (left to right)', 'AS → Add & Subtract (left to right)'],
        textbookExplanation: `The Order of Operations is a universal agreement that tells us the sequence in which to perform mathematical operations so that everyone gets the same answer.

**PEMDAS — The Standard Order:**

**P — Parentheses (and all grouping symbols)**
Evaluate everything inside parentheses, brackets [ ], braces { }, and absolute value | | first.
Example: (3 + 5) × 2 → evaluate (3+5) = 8 first → 8 × 2 = 16

**E — Exponents (and roots)**
After parentheses, evaluate all powers and roots.
Example: 2³ = 8, √9 = 3

**M/D — Multiplication and Division (left to right)**
These have equal priority — work from LEFT to RIGHT.
Example: 12 ÷ 4 × 3 = 3 × 3 = 9 (NOT 12 ÷ 12 = 1)

**A/S — Addition and Subtraction (left to right)**
These have equal priority — work from LEFT to RIGHT.
Example: 10 − 3 + 2 = 7 + 2 = 9 (NOT 10 − 5 = 5)

**Common Mistakes to Avoid:**
- Multiplication does NOT always come before division
- Addition does NOT always come before subtraction
- M/D and A/S are done left to right within their groups`,
        shortMethod: `**Mr. Augustine's "Please Excuse My Dear Aunt Sally" + Left-Right Rule:**

Say the phrase: **P-E-M-D-A-S**
"Please Excuse My Dear Aunt Sally"

**The 3-Step Attack:**
1. **Clear the brackets** — work innermost to outermost
2. **Kill the powers** — do all exponents/roots
3. **Left to right sweep** — multiply/divide as you go, then add/subtract as you go

**Key Shortcut:** When you see × and ÷ together, or + and − together, just go LEFT TO RIGHT — don't overthink it!

**Example:** 20 ÷ 5 × 2
→ Left to right: (20 ÷ 5) × 2 = 4 × 2 = **8** ✓ (NOT 20 ÷ 10 = 2 ✗)`,
        workedExamples: [
          {
            question: 'Evaluate: 3 + 4 × 2',
            textbookSteps: ['No parentheses or exponents', 'Multiplication before addition: 4 × 2 = 8', 'Now add: 3 + 8 = 11'],
            shortcut: 'Multiply first, then add: 3 + 12 = 15',
            answer: '11',
          },
          {
            question: 'Evaluate: (5 + 3)² − 4 × 3',
            textbookSteps: ['Parentheses first: 5 + 3 = 8', 'Exponent: 8² = 64', 'Multiplication: 4 × 3 = 12', 'Subtraction: 64 − 12 = 52'],
            shortcut: 'P first: 8² = 64. Then 4×3=12. Finally 64−12 = 52.',
            answer: '52',
          },
        ],
        practiceQuestions: [
          { question: 'Evaluate: 2 + 3 × 4', steps: ['No parentheses or exponents', 'Multiply: 3 × 4 = 12', 'Add: 2 + 12 = 14'], shortcut: 'Multiply before add: 2 + 12 = 14', answer: '14' },
          { question: 'Evaluate: (6 + 2) × 3', steps: ['Parentheses: 6 + 2 = 8', 'Multiply: 8 × 3 = 24'], shortcut: 'Brackets first: 8 × 3 = 24', answer: '24' },
          { question: 'Evaluate: 10 − 4 + 2', steps: ['No parentheses, exponents, or multiply/divide', 'Left to right: 10 − 4 = 6', 'Then: 6 + 2 = 8'], shortcut: 'Left to right: 10−4=6, then 6+2=8', answer: '8' },
          { question: 'Evaluate: 2³ + 5', steps: ['Exponent: 2³ = 8', 'Add: 8 + 5 = 13'], shortcut: 'Power first: 8 + 5 = 13', answer: '13' },
          { question: 'Evaluate: 18 ÷ 3 × 2', steps: ['No parentheses or exponents', 'Left to right (÷ and × equal priority): 18 ÷ 3 = 6', 'Then: 6 × 2 = 12'], shortcut: 'Left to right: 18÷3=6, then 6×2=12', answer: '12' },
          { question: 'Evaluate: 5 + (3² − 1) × 2', steps: ['Parentheses — exponent inside first: 3² = 9', 'Inside parentheses: 9 − 1 = 8', 'Multiply: 8 × 2 = 16', 'Add: 5 + 16 = 21'], shortcut: 'P: (9−1)=8. Then 8×2=16. Then 5+16=21.', answer: '21' },
          { question: 'Evaluate: 4 × 3 − 2 × 5', steps: ['Multiply left to right: 4 × 3 = 12', 'Multiply: 2 × 5 = 10', 'Subtract: 12 − 10 = 2'], shortcut: 'Do both multiplications first: 12 − 10 = 2', answer: '2' },
          { question: 'Evaluate: 3 + 4² ÷ 8', steps: ['Exponent: 4² = 16', 'Divide: 16 ÷ 8 = 2', 'Add: 3 + 2 = 5'], shortcut: 'Power: 16. Divide: 2. Add: 3+2=5.', answer: '5' },
          { question: 'Evaluate: (2 + 3)² × 2 − 10', steps: ['Parentheses: 2 + 3 = 5', 'Exponent: 5² = 25', 'Multiply: 25 × 2 = 50', 'Subtract: 50 − 10 = 40'], shortcut: 'P: 5. E: 25. M: 50. S: 40.', answer: '40' },
          { question: 'Evaluate: 24 ÷ (2 + 4) × 3', steps: ['Parentheses: 2 + 4 = 6', 'Left to right: 24 ÷ 6 = 4', 'Multiply: 4 × 3 = 12'], shortcut: 'P: 6. Then left to right: 24÷6=4, 4×3=12.', answer: '12' },
          { question: 'Evaluate: 7 + 3 × (8 − 5)', steps: ['Parentheses: 8 − 5 = 3', 'Multiply: 3 × 3 = 9', 'Add: 7 + 9 = 16'], shortcut: 'P: 3. M: 9. A: 16.', answer: '16' },
          { question: 'Evaluate: 2 × 3² − 4', steps: ['Exponent: 3² = 9', 'Multiply: 2 × 9 = 18', 'Subtract: 18 − 4 = 14'], shortcut: 'E: 9. M: 18. S: 14.', answer: '14' },
          { question: 'Evaluate: 15 − 3 × 2 + 1', steps: ['Multiply: 3 × 2 = 6', 'Left to right: 15 − 6 = 9', 'Add: 9 + 1 = 10'], shortcut: 'M first: 6. Then left to right: 15−6+1=10.', answer: '10' },
          { question: 'Evaluate: (4 + 2) ÷ 3 × 4', steps: ['Parentheses: 4 + 2 = 6', 'Left to right: 6 ÷ 3 = 2', 'Multiply: 2 × 4 = 8'], shortcut: 'P: 6. Left to right: 6÷3=2, 2×4=8.', answer: '8' },
          { question: 'Evaluate: 5² − (3 + 2) × 4', steps: ['Parentheses: 3 + 2 = 5', 'Exponent: 5² = 25', 'Multiply: 5 × 4 = 20', 'Subtract: 25 − 20 = 5'], shortcut: 'P: 5. E: 25. M: 20. S: 5.', answer: '5' },
          { question: 'Evaluate: 3 × (2 + 4)² ÷ 9', steps: ['Parentheses: 2 + 4 = 6', 'Exponent: 6² = 36', 'Left to right: 3 × 36 = 108', 'Divide: 108 ÷ 9 = 12'], shortcut: 'P: 6. E: 36. Left to right: 3×36÷9 = 108÷9 = 12.', answer: '12' },
          { question: 'Evaluate: 8 + 2 × 0 − 1', steps: ['Multiply: 2 × 0 = 0', 'Left to right: 8 + 0 = 8', 'Subtract: 8 − 1 = 7'], shortcut: 'Anything × 0 = 0. So 8 + 0 − 1 = 7.', answer: '7' },
          { question: 'Evaluate: (10 − 4)² ÷ 4 + 3', steps: ['Parentheses: 10 − 4 = 6', 'Exponent: 6² = 36', 'Divide: 36 ÷ 4 = 9', 'Add: 9 + 3 = 12'], shortcut: 'P: 6. E: 36. D: 9. A: 12.', answer: '12' },
          { question: 'Evaluate: 2 + 3 × [4 + (6 ÷ 2)]', steps: ['Innermost parentheses: 6 ÷ 2 = 3', 'Brackets: 4 + 3 = 7', 'Multiply: 3 × 7 = 21', 'Add: 2 + 21 = 23'], shortcut: 'Work inside out: 6÷2=3, 4+3=7, 3×7=21, 2+21=23.', answer: '23' },
          { question: 'Evaluate: 4³ ÷ (2² × 2)', steps: ['Exponents: 4³ = 64, 2² = 4', 'Inside parentheses: 4 × 2 = 8', 'Divide: 64 ÷ 8 = 8'], shortcut: 'E: 64 and 4. P: 4×2=8. D: 64÷8=8.', answer: '8' },
        ],
      },
    ],
  },
  {
    id: 'linear-equations',
    title: 'Solving Linear Equations',
    icon: 'ri-equal-line',
    color: 'teal',
    lessons: [
      {
        id: 'one-step-equations',
        title: 'One-Step Equations',
        subtitle: 'Solving equations with one operation',
        icon: 'ri-arrow-right-line',
        color: 'teal',
        keyFormulas: ['x + a = b → x = b − a', 'x − a = b → x = b + a', 'ax = b → x = b/a', 'x/a = b → x = ab'],
        textbookExplanation: `A one-step equation requires exactly one operation to isolate the variable.

**The Golden Rule of Equations:**
Whatever you do to one side, you MUST do to the other side. This keeps the equation balanced.

**Case 1: Addition Equation (x + a = b)**
To undo addition, subtract the same number from both sides.
Example: x + 5 = 12
Subtract 5 from both sides: x + 5 − 5 = 12 − 5
Solution: x = 7

**Case 2: Subtraction Equation (x − a = b)**
To undo subtraction, add the same number to both sides.
Example: x − 3 = 8
Add 3 to both sides: x − 3 + 3 = 8 + 3
Solution: x = 11

**Case 3: Multiplication Equation (ax = b)**
To undo multiplication, divide both sides by the coefficient.
Example: 4x = 20
Divide both sides by 4: 4x/4 = 20/4
Solution: x = 5

**Case 4: Division Equation (x/a = b)**
To undo division, multiply both sides by the divisor.
Example: x/3 = 7
Multiply both sides by 3: (x/3) × 3 = 7 × 3
Solution: x = 21

**Always check your answer** by substituting back into the original equation!`,
        shortMethod: `**Mr. Augustine's "Opposite Operation" Rule:**

The variable is trapped — free it by doing the OPPOSITE operation!

| Trapped by | Free it with |
|---|---|
| + (addition) | − (subtract) |
| − (subtraction) | + (add) |
| × (multiply) | ÷ (divide) |
| ÷ (divide) | × (multiply) |

**3-Second Method:**
1. Look at what's being done TO x
2. Do the OPPOSITE to BOTH sides
3. Simplify — done!

**Example:** x + 7 = 15
→ x is being ADDED to 7
→ SUBTRACT 7 from both sides
→ x = 15 − 7 = **8** ✓`,
        workedExamples: [
          {
            question: 'Solve: x + 9 = 14',
            textbookSteps: ['Identify the operation: addition (+9)', 'Apply inverse operation: subtract 9 from both sides', 'x + 9 − 9 = 14 − 9', 'x = 5', 'Check: 5 + 9 = 14 ✓'],
            shortcut: 'x is +9, so subtract 9: x = 14 − 9 = 5',
            answer: 'x = 5',
          },
          {
            question: 'Solve: 6x = 42',
            textbookSteps: ['Identify the operation: multiplication (×6)', 'Apply inverse operation: divide both sides by 6', '6x/6 = 42/6', 'x = 7', 'Check: 6(7) = 42 ✓'],
            shortcut: 'x is ×6, so divide: x = 42 ÷ 6 = 7',
            answer: 'x = 7',
          },
        ],
        practiceQuestions: [
          { question: 'Solve: x + 4 = 10', steps: ['Subtract 4 from both sides', 'x + 4 − 4 = 10 − 4', 'x = 6', 'Check: 6 + 4 = 10 ✓'], shortcut: 'x = 10 − 4 = 6', answer: 'x = 6' },
          { question: 'Solve: x − 7 = 3', steps: ['Add 7 to both sides', 'x − 7 + 7 = 3 + 7', 'x = 10', 'Check: 10 − 7 = 3 ✓'], shortcut: 'x = 3 + 7 = 10', answer: 'x = 10' },
          { question: 'Solve: 5x = 35', steps: ['Divide both sides by 5', '5x/5 = 35/5', 'x = 7', 'Check: 5(7) = 35 ✓'], shortcut: 'x = 35 ÷ 5 = 7', answer: 'x = 7' },
          { question: 'Solve: x/4 = 6', steps: ['Multiply both sides by 4', '(x/4) × 4 = 6 × 4', 'x = 24', 'Check: 4(24) = 24 ✓'], shortcut: 'x = 6 × 4 = 24', answer: 'x = 24' },
          { question: 'Solve: x + 15 = 22', steps: ['Subtract 15 from both sides', 'x = 22 − 15', 'x = 7', 'Check: 7 + 15 = 22 ✓'], shortcut: 'x = 22 − 15 = 7', answer: 'x = 7' },
          { question: 'Solve: x − 11 = 4', steps: ['Add 11 to both sides', 'x = 4 + 11', 'x = 15', 'Check: 15 − 11 = 4 ✓'], shortcut: 'x = 4 + 11 = 15', answer: 'x = 15' },
          { question: 'Solve: 8x = 56', steps: ['Divide both sides by 8', 'x = 56 ÷ 8', 'x = 7', 'Check: 8(7) = 56 ✓'], shortcut: 'x = 56 ÷ 8 = 7', answer: 'x = 7' },
          { question: 'Solve: x/5 = 9', steps: ['Multiply both sides by 5', 'x = 9 × 5', 'x = 45', 'Check: 45/5 = 9 ✓'], shortcut: 'x = 9 × 5 = 45', answer: 'x = 45' },
          { question: 'Solve: x + 3 = −2', steps: ['Subtract 3 from both sides', 'x = −2 − 3', 'x = −5', 'Check: −5 + 3 = −2 ✓'], shortcut: 'x = −2 − 3 = −5', answer: 'x = −5' },
          { question: 'Solve: −3x = 18', steps: ['Divide both sides by −3', 'x = 18 ÷ (−3)', 'x = −6', 'Check: −3(−6) = 18 ✓'], shortcut: 'x = 18 ÷ (−3) = −6', answer: 'x = −6' },
          { question: 'Solve: x − 5 = −9', steps: ['Add 5 to both sides', 'x = −9 + 5', 'x = −4', 'Check: −4 − 5 = −9 ✓'], shortcut: 'x = −9 + 5 = −4', answer: 'x = −4' },
          { question: 'Solve: x/7 = −3', steps: ['Multiply both sides by 7', 'x = −3 × 7', 'x = −21', 'Check: −21/7 = −3 ✓'], shortcut: 'x = −3 × 7 = −21', answer: 'x = −21' },
          { question: 'Solve: 12 = x + 5', steps: ['Rewrite: x + 5 = 12', 'Subtract 5 from both sides', 'x = 12 − 5 = 7', 'Check: 7 + 5 = 12 ✓'], shortcut: 'Flip it: x = 12 − 5 = 7', answer: 'x = 7' },
          { question: 'Solve: −x = 8', steps: ['−x means −1 × x', 'Divide both sides by −1', 'x = 8 ÷ (−1) = −8', 'Check: −(−8) = 8 ✓'], shortcut: '−x = 8 → x = −8 (flip the sign)', answer: 'x = −8' },
          { question: 'Solve: 2.5x = 10', steps: ['Divide both sides by 2.5', 'x = 10 ÷ 2.5', 'x = 4', 'Check: 2.5(4) = 10 ✓'], shortcut: 'x = 10 ÷ 2.5 = 4', answer: 'x = 4' },
          { question: 'Solve: x + (−6) = 2', steps: ['x + (−6) = x − 6 = 2', 'Add 6 to both sides', 'x = 2 + 6 = 8', 'Check: 8 − 6 = 2 ✓'], shortcut: 'x − 6 = 2 → x = 2 + 6 = 8', answer: 'x = 8' },
          { question: 'Solve: x/3 = −4', steps: ['Multiply both sides by 3', 'x = −4 × 3', 'x = −12', 'Check: −12/3 = −4 ✓'], shortcut: 'x = −4 × 3 = −12', answer: 'x = −12' },
          { question: 'Solve: 9 + x = 20', steps: ['Subtract 9 from both sides', 'x = 20 − 9', 'x = 11', 'Check: 9 + 11 = 20 ✓'], shortcut: 'x = 20 − 9 = 11', answer: 'x = 11' },
          { question: 'Solve: −4x = −28', steps: ['Divide both sides by −4', 'x = −28 ÷ (−4)', 'x = 7', 'Check: −4(7) = −28 ✓'], shortcut: 'Negatives cancel: x = 28 ÷ 4 = 7', answer: 'x = 7' },
          { question: 'Solve: x − (−3) = 10', steps: ['x − (−3) = x + 3 = 10', 'Subtract 3 from both sides', 'x = 10 − 3 = 7', 'Check: 7 + 3 = 10 ✓'], shortcut: 'Minus a negative = plus: x + 3 = 10 → x = 7', answer: 'x = 7' },
        ],
      },
      {
        id: 'two-step-equations',
        title: 'Two-Step Equations',
        subtitle: 'Solving equations with two operations',
        icon: 'ri-arrow-right-double-line',
        color: 'teal',
        keyFormulas: ['ax + b = c → x = (c − b)/a', 'x/a + b = c → x = a(c − b)', 'Step 1: Undo + or −', 'Step 2: Undo × or ÷'],
        textbookExplanation: `A two-step equation requires two operations to isolate the variable. We always undo operations in REVERSE order of PEMDAS — addition/subtraction first, then multiplication/division.

**Standard Form: ax + b = c**

**Step 1:** Undo the addition or subtraction (move the constant to the right side)
**Step 2:** Undo the multiplication or division (isolate x completely)

**Example 1:** 2x + 3 = 11
- Step 1: Subtract 3 from both sides → 2x = 8
- Step 2: Divide both sides by 2 → x = 4
- Check: 2(4) + 3 = 8 + 3 = 11 ✓

**Example 2:** x/3 − 5 = 1
- Step 1: Add 5 to both sides → x/3 = 6
- Step 2: Multiply both sides by 3 → x = 18
- Check: 18/3 − 5 = 6 − 5 = 1 ✓

**Why reverse order?**
Think of x as being "wrapped up" — the last thing done to it must be undone first. If x was multiplied then added to, we subtract first, then divide.`,
        shortMethod: `**Mr. Augustine's "Unwrap the Gift" Method:**

Think of x as a gift wrapped in layers. Unwrap from the OUTSIDE IN:

**Layer 1 (outside):** The + or − → undo it first
**Layer 2 (inside):** The × or ÷ → undo it second

**Formula to memorize:**
ax + b = c → **x = (c − b) ÷ a**

Just: subtract b, then divide by a. Two moves, done!

**Negative coefficient trick:** If it's −2x + 5 = 1
→ Subtract 5: −2x = −4
→ Divide by −2: x = 2 (negatives cancel!)`,
        workedExamples: [
          {
            question: 'Solve: 3x + 5 = 17',
            textbookSteps: ['Subtract 5 from both sides: 3x + 5 − 5 = 17 − 5', '3x = 12', 'Divide both sides by 3: 3x/3 = 12/3', 'x = 4', 'Check: 3(4) + 5 = 12 + 5 = 17 ✓'],
            shortcut: 'x = (17 − 5) ÷ 3 = 12 ÷ 3 = 4',
            answer: 'x = 4',
          },
          {
            question: 'Solve: x/2 − 3 = 7',
            textbookSteps: ['Add 3 to both sides: x/2 = 10', 'Multiply both sides by 2: x = 20', 'Check: 20/2 − 3 = 10 − 3 = 7 ✓'],
            shortcut: 'Add 3: x/2 = 10. Multiply by 2: x = 20.',
            answer: 'x = 20',
          },
        ],
        practiceQuestions: [
          { question: 'Solve: y = 3x and x + y = 8', steps: ['Substitute y=3x into x+y=8', 'x+3x=8 → 4x=8 → x=2', 'y=3(2)=6', 'Check: 2+6=8 ✓'], shortcut: 'Plug y=3x: 4x=8 → x=2, y=6.', answer: 'x = 2, y = 6' },
          { question: 'Solve: y = x − 1 and x + y = 7', steps: ['Substitute: x+(x−1)=7', '2x−1=7 → 2x=8 → x=4', 'y=4−1=3'], shortcut: 'Plug: 2x−1=7 → x=4, y=3.', answer: 'x = 4, y = 3' },
          { question: 'Solve: x = 2y and 3x − y = 15', steps: ['Substitute x=2y: 3(2y)−y=15', '6y−y=15 → 5y=15 → y=3', 'x=2(3)=6'], shortcut: 'Plug x=2y: 5y=15 → y=3, x=6.', answer: 'x = 6, y = 3' },
          { question: 'Solve: y = 2x + 3 and y = x + 5', steps: ['Set equal: 2x+3 = x+5', 'x = 2', 'y = 2(2)+3 = 7'], shortcut: 'Both equal y: 2x+3=x+5 → x=2, y=7.', answer: 'x = 2, y = 7' },
          { question: 'Solve: x + 2y = 10 and x = y + 1', steps: ['Substitute x=y+1: (y+1)+2y=10', '3y+1=10 → 3y=9 → y=3', 'x=3+1=4'], shortcut: 'Plug x=y+1: 3y=9 → y=3, x=4.', answer: 'x = 4, y = 3' },
          { question: 'Solve: y = 3x − 2 and 2x + y = 8', steps: ['Substitute: 2x + (3x−2) = 8', '5x−2 = 8 → 5x = 10 → x = 2', 'y = 3(2)−2 = 4'], shortcut: 'Plug: 5x−2=8 → x=2, y=4.', answer: 'x = 2, y = 4' },
          { question: 'Solve: x + 4 = 9 and x + y = 7', steps: ['Subtract 4: x = 5', '5 + y = 7 → y = 2'], shortcut: 'x=5 from first. Then y=2.', answer: 'x = 5, y = 2' },
          { question: 'Solve: y = x and x + y = 10', steps: ['Substitute y=x: x+x=10', '2x=10 → x=5', 'y=5'], shortcut: 'y=x means both equal: 2x=10 → x=y=5.', answer: 'x = 5, y = 5' },
          { question: 'Solve: y = x + 4 and x + y = 10', steps: ['Substitute y=x+4: x+(x+4)=10', '2x+4=10 → 2x=6 → x=3', 'y=3+4=7'], shortcut: 'Plug y=x+4: 2x+4=10 → x=3, y=7.', answer: 'x = 3, y = 7' },
          { question: 'Solve: x + 2y = 10 and x = y + 1', steps: ['Substitute x=y+1: (y+1)+2y=10', '3y+1=10 → 3y=9 → y=3', 'x=3+1=4'], shortcut: 'Plug x=y+1: 3y=9 → y=3, x=4.', answer: 'x = 4, y = 3' },
          // ... (remaining practice questions omitted for brevity) ...
          { question: 'Solve: x + 2y = 10 and x = y + 1', steps: ['Substitute x=y+1: (y+1)+2y=10', '3y+1=10 → 3y=9 → y=3', 'x=3+1=4'], shortcut: 'Plug x=y+1: 3y=9 → y=3, x=4.', answer: 'x = 4, y = 3' },
        ],
      },
    ],
  },
];
